import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  DatePicker,
  Select,
  Input,
  Button,
  Spin,
  Empty,
  Segmented,
  Progress,
  notification,
} from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import { numberWithCommas } from "util/common";
import {
  AI_CREDIT_SOURCE_LABEL_KEYS,
  AI_CREDIT_ACTION_LABEL_KEYS,
  AI_CREDIT_DETAIL_LABEL_KEYS,
  formatAiCreditRunDate,
  formatResetDate,
  resolveSummaryResetDate,
  resolveSummaryDaysUntilReset,
  formatCreditUsedLabel,
  buildHumanCreditDetailRows,
  buildCreditRecordLinks,
  formatAiCreditLlmLabel,
  formatAiCreditVendorLabel,
  formatAiCreditMetricLabel,
  formatAiCreditDateRangeLabel,
} from "util/aiCreditHistoryDisplay";
import styles from "./style.module.scss";

const { RangePicker } = DatePicker;

const SOURCE_BADGE_CLASS = {
  my_workspace: styles.sourceBadgeWorkspace,
  monthly_ai_report: styles.sourceBadgeReport,
  dashboard: styles.sourceBadgeDashboard,
  other: styles.sourceBadgeOther,
};

const Representation = ({ user, locale = "tr-TR" }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0 });
  const [filterUsers, setFilterUsers] = useState([]);
  const [forbidden, setForbidden] = useState(false);

  const [periodPreset, setPeriodPreset] = useState("cycle");
  const [dateRange, setDateRange] = useState(null);
  const [filterUserId, setFilterUserId] = useState(undefined);
  const [sourceFilter, setSourceFilter] = useState(undefined);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");

  const sourceFilterToActionTypes = useMemo(() => {
    if (!sourceFilter) return undefined;
    const map = {
      my_workspace: ["welcome_widget"],
      monthly_ai_report: ["ai_report"],
      dashboard: ["root_cause"],
      other: ["ad_hoc"],
    };
    return map[sourceFilter];
  }, [sourceFilter]);

  const effectiveDateRange = periodPreset === "custom" ? dateRange : null;

  const fetchHistory = useCallback(
    async (page = 1) => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const payload = {
          user_id: user.uid,
          page,
          page_size: 20,
        };
        if (effectiveDateRange?.[0]) {
          payload.start_date = effectiveDateRange[0].format("YYYY-MM-DD");
        }
        if (effectiveDateRange?.[1]) {
          payload.end_date = effectiveDateRange[1].format("YYYY-MM-DD");
        }
        if (filterUserId) {
          payload.filter_user_id = filterUserId;
        }
        if (sourceFilterToActionTypes?.length) {
          payload.action_types = sourceFilterToActionTypes;
        }
        if (search.trim()) {
          payload.search = search.trim();
        }

        const result = await apiRequest(
          "subscriptions/ai_credit_history",
          "POST",
          payload,
          true,
        );

        if (result?.success) {
          setSummary(result.summary);
          setItems(result.items || []);
          setPagination(result.pagination || { page: 1, page_size: 20, total: 0 });
          setFilterUsers(result.filter_users || []);
          setForbidden(false);
        } else if (
          result?.error &&
          String(result.error).toUpperCase().includes("FORBIDDEN")
        ) {
          setForbidden(true);
        } else {
          notification.error({
            message: result?.error || t("ai_credits_page.loadError"),
          });
        }
      } catch {
        notification.error({ message: t("ai_credits_page.loadError") });
      } finally {
        setLoading(false);
      }
    },
    [
      user?.uid,
      effectiveDateRange,
      filterUserId,
      sourceFilterToActionTypes,
      search,
      t,
    ],
  );

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  const quotaPercent = useMemo(() => {
    if (!summary?.total_quota) return 0;
    return Math.min(
      100,
      Math.round((Number(summary.used_this_period || 0) / summary.total_quota) * 100),
    );
  }, [summary]);

  const summaryResetDate = useMemo(
    () => resolveSummaryResetDate(summary),
    [summary],
  );
  const summaryDaysUntilReset = useMemo(
    () => resolveSummaryDaysUntilReset(summary),
    [summary],
  );

  const renderSourceBadge = (source) => {
    const key = AI_CREDIT_SOURCE_LABEL_KEYS[source] || AI_CREDIT_SOURCE_LABEL_KEYS.other;
    const className = SOURCE_BADGE_CLASS[source] || styles.sourceBadgeOther;
    return <span className={className}>{t(key)}</span>;
  };

  const renderExpandedRow = (record) => {
    const detailRows = buildHumanCreditDetailRows(record, t);
    const links = buildCreditRecordLinks(record, t);

    if (!detailRows.length && !links.length) {
      return (
        <div className={styles.expandedPanel}>
          <p className={styles.expandedEmpty}>{t("ai_credits_page.detailEmpty")}</p>
        </div>
      );
    }

    return (
      <div className={styles.expandedPanel}>
        {detailRows.length > 0 ? (
          <div className={styles.detailCard}>
            <p className={styles.detailCardTitle}>{t("ai_credits_page.detailTitle")}</p>
            <div className={styles.detailGrid}>
              {detailRows.map((row) => {
                const labelKey = AI_CREDIT_DETAIL_LABEL_KEYS[row.label];
                return (
                  <React.Fragment key={row.label}>
                    <div className={styles.detailLabel}>
                      {labelKey ? t(labelKey) : row.label}
                    </div>
                    <div className={styles.detailValue}>{row.value}</div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        ) : null}
        {(record.input_tokens > 0 || record.output_tokens > 0) && (
          <div className={styles.tokenLine}>
            {t("ai_credits_page.tokensLine", {
              input: numberWithCommas(record.input_tokens),
              output: numberWithCommas(record.output_tokens),
            })}
          </div>
        )}
        {(record.llm_provider || record.llm_model) && (
          <div className={styles.tokenLine}>
            {t("ai_credits_page.colLlm")}:{" "}
            {formatAiCreditLlmLabel(record.llm_provider, record.llm_model)}
          </div>
        )}
        {links.length > 0 ? (
          <div className={styles.linkRow}>
            {links.map((link) => (
              <span key={link} className={styles.linkRef}>
                {link}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const columns = [
    {
      title: t("ai_credits_page.colDate"),
      dataIndex: "run_date",
      key: "run_date",
      width: 160,
      render: (val) => formatAiCreditRunDate(val, locale),
    },
    {
      title: t("ai_credits_page.colUser"),
      key: "user",
      width: 180,
      render: (_, record) =>
        record.user_display ? (
          <div className={styles.userCell}>
            <span className={styles.userName}>{record.user_display}</span>
            {record.user_email ? (
              <span className={styles.userEmail}>{record.user_email}</span>
            ) : null}
          </div>
        ) : (
          <span className={styles.systemUser}>{t("ai_credits_page.systemUser")}</span>
        ),
    },
    {
      title: t("ai_credits_page.colSource"),
      dataIndex: "source",
      key: "source",
      width: 150,
      render: (source) => renderSourceBadge(source),
    },
    {
      title: t("ai_credits_page.colAction"),
      dataIndex: "action_key",
      key: "action_key",
      width: 140,
      render: (key) =>
        t(AI_CREDIT_ACTION_LABEL_KEYS[key] || AI_CREDIT_ACTION_LABEL_KEYS.ad_hoc_request),
    },
    {
      title: t("ai_credits_page.colVendor"),
      key: "vendor_label",
      width: 160,
      ellipsis: true,
      render: (_, record) => (
        <span className={styles.metaCell}>{formatAiCreditVendorLabel(record)}</span>
      ),
    },
    {
      title: t("ai_credits_page.colMetric"),
      key: "metric_label",
      width: 150,
      ellipsis: true,
      render: (_, record) => (
        <span className={styles.metaCell}>
          {formatAiCreditMetricLabel(record, t)}
        </span>
      ),
    },
    {
      title: t("ai_credits_page.colDateRange"),
      key: "date_range_label",
      width: 170,
      ellipsis: true,
      render: (_, record) => (
        <span className={styles.metaCell}>
          {formatAiCreditDateRangeLabel(record)}
        </span>
      ),
    },
    {
      title: t("ai_credits_page.colLlm"),
      key: "llm",
      width: 150,
      ellipsis: true,
      render: (_, record) => (
        <span className={styles.metaCell}>
          {formatAiCreditLlmLabel(record.llm_provider, record.llm_model)}
        </span>
      ),
    },
    {
      title: t("ai_credits_page.colUsed"),
      dataIndex: "credits_used",
      key: "credits_used",
      width: 110,
      align: "right",
      render: (val) => (
        <span className={styles.creditsUsed}>{formatCreditUsedLabel(val, t)}</span>
      ),
    },
    {
      title: t("ai_credits_page.colRemaining"),
      dataIndex: "remaining_after",
      key: "remaining_after",
      width: 100,
      align: "right",
      render: (val) => (
        <span className={styles.creditsRemaining}>{numberWithCommas(val)}</span>
      ),
    },
  ];

  if (forbidden) {
    return (
      <div className={styles.forbidden}>
        <h4 className={styles.title}>{t("ai_credits_page.title")}</h4>
        <p className={styles.subtitle}>{t("ai_credits_page.orgRequired")}</p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <h4 className={styles.title}>{t("ai_credits_page.title")}</h4>
      <p className={styles.subtitle}>{t("ai_credits_page.subtitle")}</p>

      <div className={styles.summaryHero}>
        <div className={styles.summaryHeroMain}>
          <span className={styles.summaryHeroLabel}>
            {t("ai_credits_page.summaryRemaining")}
          </span>
          <span className={styles.summaryHeroValue}>
            {summary ? numberWithCommas(summary.remaining) : "—"}
          </span>
          {summaryResetDate ? (
            <span className={styles.summaryHeroMeta}>
              {t("ai_credits_page.resetOn", {
                date: formatResetDate(summaryResetDate, locale),
              })}
              {summaryDaysUntilReset != null ? (
                <>
                  {" · "}
                  {t("ai_credits_page.resetInDays", { count: summaryDaysUntilReset })}
                </>
              ) : null}
            </span>
          ) : null}
          {summary ? (
            <Progress
              percent={quotaPercent}
              showInfo={false}
              strokeColor="#6b21a8"
              trailColor="rgba(107, 33, 168, 0.12)"
              strokeWidth={6}
              className={styles.summaryProgress}
            />
          ) : null}
        </div>
        <div className={styles.summarySide}>
          <div className={styles.summarySideItem}>
            <span className={styles.summarySideLabel}>
              {t("ai_credits_page.summaryQuota")}
            </span>
            <span className={styles.summarySideValue}>
              {summary ? numberWithCommas(summary.total_quota) : "—"}
            </span>
          </div>
          <div className={styles.summarySideItem}>
            <span className={styles.summarySideLabel}>
              {t("ai_credits_page.summaryUsed")}
            </span>
            <span className={styles.summarySideValueUsed}>
              {summary ? numberWithCommas(summary.used_this_period) : "—"}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <Segmented
          value={periodPreset}
          onChange={(val) => {
            setPeriodPreset(val);
            if (val !== "custom") {
              setDateRange(null);
            }
          }}
          options={[
            { label: t("ai_credits_page.periodCycle"), value: "cycle" },
            { label: t("ai_credits_page.periodCustom"), value: "custom" },
          ]}
        />
        {periodPreset === "custom" ? (
          <RangePicker
            value={dateRange}
            onChange={(vals) => setDateRange(vals)}
            allowClear
            format="YYYY-MM-DD"
          />
        ) : null}
        <Select
          allowClear
          placeholder={t("ai_credits_page.filterUser")}
          className={styles.filterSelect}
          value={filterUserId}
          onChange={setFilterUserId}
          options={filterUsers.map((u) => ({
            value: u.user_id,
            label: u.label || u.email || u.user_id,
          }))}
        />
        <Select
          allowClear
          placeholder={t("ai_credits_page.filterSource")}
          className={styles.filterSelect}
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { value: "my_workspace", label: t(AI_CREDIT_SOURCE_LABEL_KEYS.my_workspace) },
            {
              value: "monthly_ai_report",
              label: t(AI_CREDIT_SOURCE_LABEL_KEYS.monthly_ai_report),
            },
            { value: "dashboard", label: t(AI_CREDIT_SOURCE_LABEL_KEYS.dashboard) },
            { value: "other", label: t(AI_CREDIT_SOURCE_LABEL_KEYS.other) },
          ]}
        />
        <Button
          type="text"
          icon={<ReloadOutlined />}
          className={styles.refreshBtn}
          onClick={() => fetchHistory(pagination.page)}
          aria-label={t("ai_credits_page.refresh")}
        />
      </div>

      <div className={styles.searchRow}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("ai_credits_page.searchPlaceholder")}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onPressEnter={() => setSearch(searchDraft)}
        />
        <Button type="primary" onClick={() => setSearch(searchDraft)}>
          {t("ai_credits_page.search")}
        </Button>
      </div>

      <div className={styles.tableCard}>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{
            emptyText: loading ? (
              <div className={styles.tableLoading}>
                <Spin size="small" />
              </div>
            ) : (
              <Empty description={t("ai_credits_page.emptyHistory")} />
            ),
          }}
          expandable={{
            expandedRowRender: renderExpandedRow,
            columnTitle: t("ai_credits_page.colDetails"),
            columnWidth: 72,
            expandRowByClick: true,
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: pagination.total,
            showSizeChanger: false,
            onChange: (page) => fetchHistory(page),
          }}
          scroll={{ x: 1280 }}
        />
      </div>
    </div>
  );
};

export default Representation;
